import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Result } from 'antd';

import SharedFormPage from './SharedFormPage';
import ReportPageLink from '../components/common/ReportPageLink';
import { PublicFormSkeleton } from '../components/common/PublicPageSkeleton';
import { DataState } from '../components/loading';
import { effectiveSiteKey } from '../components/common/TurnstileWidget';
import { FORM_OUT_OF_DATE_TITLE, isFormOutOfDate, useNoIndex } from './ShortLinkPage';
import {
	getPublicForm, removePublicFormUpload, reportPublicForm, submitPublicForm, uploadPublicFormFile,
} from '../services/publicForms';
import {
	describeFormReportError, isLimitReached, limitRetryText, LIMIT_TITLE, NO_FORM_TITLE, PAUSED_TEXT, UNREACHABLE_TITLE,
} from '../utils/publicForms';
import { draftKeyFor, readDraft } from '../utils/formChanges';
import { planLimitOf, VISITOR_WORDS } from '../utils/planLimit';

/** No answer, a 5xx, or a 429 that is not the form's own window: try again. */
const couldNotReach = (error) => {
	const status = error?.response?.status;
	return !status || status >= 500 || status === 429;
};

/**
 * `/f/:collectionSlug/:formSlug` — a PERMANENT PUBLIC FORM (KPR-513; keepr-api
 * docs/SCHEMA.md § Public forms, "The address takes submissions"). The
 * address on a sign on a lamp post: readable, printed, NOT secret, and with no
 * expiry. A visitor with no account fills it in and sends it; the submission
 * waits in the collection's review queue like an invite link's.
 *
 * It renders inside PublicShell, beside `/s/:code`, for the same reason: the
 * app shell's rail, palette and inbox are authenticated reads, and none of
 * them may mount around a stranger's page. Every request this page makes is a
 * plain fetch with no credentials (services/publicForms), so the person
 * signed in on this browser — if anyone is — is never sent along.
 *
 * What the address can answer, decided by the API live on every request:
 *   - the form → SharedFormPage, the invite link's own page, given this
 *     address's five calls, with a fresh bot-check token for every upload,
 *     and "Report this form" at its foot (KPR-514);
 *   - paused → "This form is paused." and NOTHING else (f04): no name, no
 *     reason (the API never says one — a spammer must not learn their flood
 *     paused it), no card;
 *   - the flat 404 → "There is no form at this address", for every way an
 *     address can be dead (unknown, retired, a draft, the collection off…);
 *   - 400 `form_out_of_date` → the form needs updating by its owner;
 *   - 429 `form_limit_reached` → it has taken all it can for now, and when
 *     to come back (said up front, so nobody fills in a form the submit
 *     would refuse).
 *
 * WHAT THE VISITOR TYPED survives the form changing under them (KPR-515; the
 * owner's ruling f09). A change while the form is open is the submit's (or
 * an upload's) 409 `form_changed`, which SharedFormPage answers in place: the
 * form reloads with the answers kept and the new questions marked. A change
 * that STOPS the form (400 `form_out_of_date`) cannot be answered in place —
 * so the page keeps the answers in this tab's sessionStorage (`draftKey`,
 * utils/formChanges), and when the owner has fixed it, Try again (or any
 * reload) opens the form with them back. Nothing is sent anywhere for it,
 * nothing is put in the URL, and a successful send clears it.
 *
 * Note `/f/:code` with ONE segment is a different route — a multi add's
 * short link, for a signed-in person, inside the shell. The two never meet:
 * this one has two segments.
 */
export default function PublicFormPage() {
	const { collectionSlug, formSlug } = useParams();
	const navigate = useNavigate();
	useNoIndex();
	// 'loading' | 'form' | 'paused' | 'missing' | 'out-of-date' | 'limit' | 'plan-limit' | 'unreachable'
	const [status, setStatus] = useState('loading');
	const [view, setView] = useState(null);
	const [limitError, setLimitError] = useState(null);
	const [attempt, setAttempt] = useState(0);

	const formApi = useMemo(() => ({
		refresh: () => getPublicForm(collectionSlug, formSlug),
		isLive: fresh => fresh?.kind === 'public' && fresh?.paused !== true && Array.isArray(fresh?.elements),
		submit: payload => submitPublicForm(collectionSlug, formSlug, payload),
		upload: (args, options) => uploadPublicFormFile(collectionSlug, formSlug, args, options),
		remove: (id, token) => removePublicFormUpload(collectionSlug, formSlug, id, token),
	}), [collectionSlug, formSlug]);

	const show = useCallback((fresh) => {
		if (fresh?.kind === 'public' && fresh?.paused === true) { setView(null); setStatus('paused'); return; }
		if (formApi.isLive(fresh)) { setView(fresh); setStatus('form'); return; }
		setView(null);
		setStatus('missing');
	}, [formApi]);

	const showError = useCallback((error) => {
		setView(null);
		if (isFormOutOfDate(error)) setStatus('out-of-date');
		else if (isLimitReached(error)) { setLimitError(error); setStatus('limit'); }
		// The owner's plan limit on submissions (409 `plan_limit`, KPR-1095):
		// the visitor is told only that the form isn't taking submissions
		// right now — never whose limit, which plan, or how far over. A SEND
		// refused the same way is said in place, answers kept (SharedFormPage).
		else if (planLimitOf(error)) setStatus('plan-limit');
		// Only the flat 404 says there is no form. No answer, a server error or
		// this network's own rate limit is a blip: a sign reader is told to try
		// again, never that the form does not exist (independent review).
		else if (couldNotReach(error)) setStatus('unreachable');
		else setStatus('missing');
	}, []);

	useEffect(() => {
		let active = true;
		setStatus('loading');
		getPublicForm(collectionSlug, formSlug)
			.then((fresh) => { if (active) show(fresh); })
			.catch((error) => { if (active) showError(error); });
		return () => { active = false; };
	}, [collectionSlug, formSlug, show, showError, attempt]);

	const handleDead = useCallback(() => { setView(null); setStatus('missing'); }, []);
	const handleOutOfDate = useCallback(() => { setView(null); setStatus('out-of-date'); }, []);
	const handlePaused = useCallback(() => { setView(null); setStatus('paused'); }, []);
	const handleLimit = useCallback((error) => { setView(null); setLimitError(error); setStatus('limit'); }, []);
	const goHome = () => navigate('/');

	const renderOutcome = () => {
		if (status === 'form' && view) {
			// "Report this form" (KPR-514): only here, on a live form — a paused,
			// dead or full address serves nothing to report (the API answers 409
			// `form_paused` to a report on a paused one). The dialog's bot check
			// uses the key the form's own does: the API's, or the build's.
			const reportLink = (
				<ReportPageLink
					label="Report this form"
					prompt="What is wrong with this form? Your report goes to keepr, not to whoever put it up."
					thanks="Thank you. Someone at keepr will look at this form."
					challenge={{ siteKey: effectiveSiteKey(view?.botCheck?.siteKey || null) }}
					onReport={payload => reportPublicForm(collectionSlug, formSlug, payload)}
					describeError={describeFormReportError}
				/>
			);
			return (
				<SharedFormPage
					// A new address is a new form: nothing typed carries across.
					key={`${collectionSlug}/${formSlug}`}
					view={view}
					formApi={formApi}
					uploadNeedsChallenge
					onDead={handleDead}
					onOutOfDate={handleOutOfDate}
					onPaused={handlePaused}
					onLimit={handleLimit}
					reportLink={reportLink}
					draftKey={draftKeyFor(collectionSlug, formSlug)}
				/>
			);
		}

		if (status === 'paused') {
			// Only the sentence (f04; AC: "a paused form shows 'This form is
			// paused.' only"). Not the collection's name or the headline the API
			// also sends: a sign that says why or whose would tell a spammer more
			// than the sentence does, and a visitor needs nothing else to act on.
			return (
				<Result
					className="public-form__paused"
					status="info"
					title={PAUSED_TEXT}
					data-testid="public-form-paused"
				/>
			);
		}

		if (status === 'out-of-date') {
			// KPR-515: the visitor's answers wait in this tab; say so only when
			// there are some.
			const kept = Boolean(readDraft(draftKeyFor(collectionSlug, formSlug)));
			return (
				<Result
					className="short-link__invalid"
					status="warning"
					title={FORM_OUT_OF_DATE_TITLE}
					subTitle={kept
						? 'What you typed is kept in this tab for two hours (not private answers or photos). Try again later, or tell whoever put up this address — once it is fixed, your answers come back.'
						: 'Try again later, or tell whoever put up this address.'}
					extra={[
						<Button key="again" type="primary" onClick={() => setAttempt(n => n + 1)}>Try again</Button>,
						<Button key="home" onClick={goHome}>Go to keepr</Button>,
					]}
					data-testid="public-form-out-of-date"
				/>
			);
		}

		if (status === 'limit') {
			return (
				<Result
					className="short-link__invalid"
					status="info"
					title={LIMIT_TITLE}
					subTitle={limitRetryText(limitError)}
					data-testid="public-form-limit"
				/>
			);
		}

		if (status === 'plan-limit') {
			return (
				<Result
					className="short-link__invalid"
					status="info"
					title={VISITOR_WORDS}
					data-testid="public-form-plan-limit"
				/>
			);
		}

		if (status === 'unreachable') {
			return (
				<Result
					className="short-link__invalid"
					status="warning"
					title={UNREACHABLE_TITLE}
					subTitle="keepr did not answer just now. Check the connection, then try again."
					extra={<Button type="primary" onClick={() => setAttempt(n => n + 1)}>Try again</Button>}
					data-testid="public-form-unreachable"
				/>
			);
		}

		if (status === 'missing') {
			return (
				<Result
					className="short-link__invalid"
					status="404"
					title={NO_FORM_TITLE}
					subTitle="Check the address, or ask whoever put it up."
					extra={<Button type="primary" onClick={goHome}>Go to keepr</Button>}
					data-testid="public-form-missing"
				/>
			);
		}
		return null;
	};

	// The wait for the form is the standard loading-initial state: field
	// skeletons in the form's own column, then the form. A 30 s silence is the
	// error with Retry, never a skeleton that stays (KPR-650).
	const waiting = status === 'loading';
	return (
		<DataState
			// A retry starts the wait afresh, with its own 30 s.
			key={attempt}
			state={waiting ? 'loading-initial' : 'ready'}
			skeleton={<PublicFormSkeleton />}
			onRetry={() => setAttempt(n => n + 1)}
			label="this form"
			className="data-state--public"
		>
			{waiting ? null : renderOutcome()}
		</DataState>
	);
}
